import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Flex, Input, Modal, Select, Skeleton, Space, Table, Tag, Typography } from 'antd';
import type { McpTokenSummary } from '@youkuai/contracts';
import { useState } from 'react';

import { useAuth } from './auth.tsx';
import { issueOwnMcpToken, listOwnMcpTokens, revokeOwnMcpToken } from './mcp-token-api.ts';
import { ApiRequestError } from './project-api.ts';

const { Paragraph, Text, Title } = Typography;

function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前会话不是 Web 登录态，不能签发 MCP 令牌。',
      MCP_ACCESS_DENIED: '系统管理员尚未为这个账号开通 AI 接入，或授权已过期/关闭。',
      MCP_TOKEN_LIMIT_REACHED: '有效令牌已达 5 个，请先吊销不用的令牌。',
      MCP_TOKEN_INVALID: '标签或有效期不合法，请检查后重试。',
      MCP_TOKEN_NOT_FOUND: '这个令牌不存在或已经吊销，请刷新列表。',
      MCP_TOKEN_UNAVAILABLE: '当前环境未配置 MCP 令牌服务。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

function tokenStatus(token: McpTokenSummary): { label: string; color: string } {
  if (token.revokedAt) return { label: '已吊销', color: 'default' };
  if (new Date(token.expiresAt).valueOf() <= Date.now()) return { label: '已过期', color: 'warning' };
  return { label: '有效', color: 'success' };
}

function formatTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export function McpAccessPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : '';
  const client = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [label, setLabel] = useState('我的 AI 客户端');
  const [days, setDays] = useState(30);
  const [issued, setIssued] = useState<{ token: string; expiresAt: string; label: string } | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const tokens = useQuery({
    queryKey: ['own-mcp-tokens', accountId],
    queryFn: () => listOwnMcpTokens(token),
    enabled: !!token,
  });
  const issueMutation = useMutation({ mutationFn: issueOwnMcpToken });
  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => revokeOwnMcpToken(token, tokenId),
  });

  const mcpBase = window.location.origin;
  const codexSnippet = `[mcp_servers.youkuai]\nurl = "${mcpBase}/mcp"\nbearer_token_env_var = "YOUKUAI_MCP_TOKEN"`;
  const workBuddySnippet = issued
    ? JSON.stringify({
      mcpServers: {
        youkuai: {
          type: 'http',
          url: `${mcpBase}/mcp`,
          headers: { Authorization: `Bearer ${issued.token}` },
        },
      },
    }, null, 2)
    : '';

  async function copy(value: string, message: string) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice({ type: 'success', text: message });
    } catch {
      setNotice({ type: 'error', text: '复制失败，请手动选择文本复制。' });
    }
  }

  async function issue() {
    try {
      const result = await issueMutation.mutateAsync({ token, label, days });
      setIssued({ token: result.token, expiresAt: result.expiresAt, label: result.label });
      setIssueOpen(false);
      setNotice({ type: 'success', text: '令牌已签发。明文只显示这一次，请立即保存。' });
      await client.invalidateQueries({ queryKey: ['own-mcp-tokens', accountId] });
    } catch (error) {
      setNotice({ type: 'error', text: errorText(error) });
    }
  }

  function revoke(row: McpTokenSummary) {
    Modal.confirm({
      title: '吊销这个令牌？',
      content: `吊销后使用「${row.label}」的 Codex / WorkBuddy 会在下一次调用时立即失效。`,
      okText: '确认吊销',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await revokeMutation.mutateAsync(row.tokenId);
          setNotice({ type: 'success', text: '令牌已吊销。' });
          await client.invalidateQueries({ queryKey: ['own-mcp-tokens', accountId] });
        } catch (error) {
          setNotice({ type: 'error', text: errorText(error) });
          throw error;
        }
      },
    });
  }

  const columns = [
    { title: '名称', dataIndex: 'label', key: 'label' },
    { title: '令牌前缀', dataIndex: 'tokenPrefix', key: 'tokenPrefix', render: (value: string) => <Text code>{value}…</Text> },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', render: (value: string) => formatTime(value) },
    { title: '到期时间', dataIndex: 'expiresAt', key: 'expiresAt', render: (value: string) => formatTime(value) },
    { title: '最近使用', dataIndex: 'lastUsedAt', key: 'lastUsedAt', render: (value: string | null) => formatTime(value) },
    {
      title: '状态',
      key: 'status',
      render: (_value: unknown, row: McpTokenSummary) => {
        const status = tokenStatus(row);
        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      render: (_value: unknown, row: McpTokenSummary) => (
        <Button size="small" danger disabled={!!row.revokedAt} loading={revokeMutation.isPending}
          onClick={() => revoke(row)}>吊销</Button>
      ),
    },
  ];

  return <main className="page-shell customer-shell">
    <div className="section-kicker">AI ACCESS / 个人访问令牌</div>
    <div className="project-heading">
      <div>
        <Title level={1}>AI 接入</Title>
        <Paragraph>
          在这里生成你自己的 MCP 访问令牌，让 Codex、WorkBuddy 等 AI 客户端以你的身份读取系统数据。
          令牌只显示一次；每个人只能管理自己的令牌。
        </Paragraph>
      </div>
      <Button type="primary" onClick={() => { setIssued(null); setNotice(null); setIssueOpen(true); }}>
        生成新令牌
      </Button>
    </div>

    {notice && <Alert className="workspace-alert" showIcon type={notice.type} title={notice.text} closable
      onClose={() => setNotice(null)} />}

    <Alert className="workspace-alert" type="info" showIcon title="令牌等同于你的登录身份"
      description="不要把令牌发给别人或提交到 Git。最多保留 5 个有效令牌；不再使用时请立即吊销。AI 客户端目前只有只读权限，写操作仍需回到 Web。" />

    <Card title="我的令牌" extra={<Button onClick={() => void tokens.refetch()}>刷新</Button>}>
      {tokens.isPending && <Skeleton active paragraph={{ rows: 4 }} />}
      {tokens.isError && <Alert type="error" showIcon title="令牌列表读取失败" description={errorText(tokens.error)} />}
      {!tokens.isPending && !tokens.isError && !tokens.data?.items.length && (
        <Empty description="还没有令牌。点击右上角「生成新令牌」开始接入。" />
      )}
      {!!tokens.data?.items.length && (
        <Table rowKey="tokenId" size="middle" pagination={false} columns={columns} dataSource={tokens.data.items} />
      )}
    </Card>

    <Card title="怎么接入" style={{ marginTop: 18 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>Codex</Text>
          <Paragraph type="secondary">把下面内容加入 <Text code>~/.codex/config.toml</Text>，并在环境变量中设置
            <Text code>YOUKUAI_MCP_TOKEN</Text>。</Paragraph>
          <pre className="mcp-snippet">{codexSnippet}</pre>
          <Button size="small" onClick={() => void copy(codexSnippet, 'Codex 配置已复制。')}>复制 Codex 配置</Button>
        </div>
        <div>
          <Text strong>WorkBuddy</Text>
          <Paragraph type="secondary">生成令牌后，把对应 JSON 写入 <Text code>~/.workbuddy/mcp.json</Text>。
            如果客户端不识别 <Text code>type: http</Text>，把地址改为 <Text code>/sse</Text> 并设置
            <Text code>type: sse</Text>。</Paragraph>
          {issued
            ? <><pre className="mcp-snippet">{workBuddySnippet}</pre>
              <Button size="small" onClick={() => void copy(workBuddySnippet, 'WorkBuddy 配置已复制。')}>复制 WorkBuddy 配置</Button></>
            : <Text type="secondary">生成令牌后会在这里显示可直接复制的 WorkBuddy 配置。</Text>}
        </div>
      </Space>
    </Card>

    <Modal title="生成新的 AI 访问令牌" open={issueOpen} onCancel={() => setIssueOpen(false)}
      onOk={() => void issue()} okText="生成令牌" cancelText="取消" confirmLoading={issueMutation.isPending}
      destroyOnHidden>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="warning" showIcon title="令牌只显示一次"
          description="生成后请立即复制到密码管理器；关闭弹窗后无法再次查看明文。" />
        <label>
          <Text strong>名称</Text>
          <Input value={label} maxLength={120} onChange={(event) => setLabel(event.target.value)}
            placeholder="例如：我的 Mac / 公司电脑 Codex" />
        </label>
        <label>
          <Text strong>有效期</Text>
          <Select value={days} onChange={setDays} style={{ width: '100%' }}
            options={[{ value: 7, label: '7 天' }, { value: 30, label: '30 天（推荐）' }, { value: 90, label: '90 天' }]} />
        </label>
        <Text type="secondary">最多保留 5 个有效令牌。建议按设备/客户端分别签发，便于单独吊销。</Text>
      </Space>
    </Modal>

    <Modal title="令牌已生成——只显示这一次" open={!!issued} onCancel={() => setIssued(null)}
      footer={<Button type="primary" onClick={() => setIssued(null)}>我已保存</Button>} width={720} destroyOnHidden>
      {issued && <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="warning" showIcon title="请立即复制并保存"
          description={`「${issued.label}」的有效期至 ${formatTime(issued.expiresAt)}。关闭后无法再次查看明文。`} />
        <div>
          <Text strong>令牌</Text>
          <Flex gap={8} style={{ marginTop: 6 }}>
            <Input readOnly value={issued.token} />
            <Button onClick={() => void copy(issued.token, '令牌已复制。')}>复制</Button>
          </Flex>
        </div>
        <div>
          <Text strong>Codex 配置</Text>
          <pre className="mcp-snippet">{codexSnippet}</pre>
        </div>
        <div>
          <Text strong>WorkBuddy 配置</Text>
          <pre className="mcp-snippet">{workBuddySnippet}</pre>
        </div>
      </Space>}
    </Modal>
  </main>;
}
